import { SmartFilters, SmartTable } from '@certemy/ui-components';
import { TO_DO_COMPLIANCE_STATUS } from 'interfaces/testing-workflow.interface';
import { TodoListCustomFilter, TodoListResponse } from '../todo.interface';
import { PolicyObj } from 'services/policy-permission/policy-permissions.service';
import { WORKFLOW_MODULE_ID } from 'constants/workflow.constants';

export const DefaultTableData: TodoListResponse = {
  rows: [],
  count: 0,
};

export const DEFAULT_STEP_DUE_DATE_RANGE_FILTER_VALUE = 30;

export const DEFAULT_STEP_DUE_DATE_RANGE_FILTER_OPTIONS = [
  { id: 30, name: '30 days' },
  { id: 60, name: '60 days' },
  { id: 90, name: '90 days' },
  { id: 180, name: '180 days' },
];

export const TESTING_DEFAULT_STEP_DUE_DATE_RANGE_FILTER_OPTIONS = [
  ...DEFAULT_STEP_DUE_DATE_RANGE_FILTER_OPTIONS,
  { id: 'All', name: 'All' },
];
export const COMPLIANCE_DEFAULT_STEP_DUE_DATE_RANGE_FILTER_OPTIONS = [
  ...DEFAULT_STEP_DUE_DATE_RANGE_FILTER_OPTIONS,
  { id: 0, name: 'All' },
];

const DEFAULT_SORT_OPTION = {
  ['certemy-tasks']: {
    field: 'updatedAt',
    direction: SmartTable.Order.DESC,
  },
  [WORKFLOW_MODULE_ID.TESTING_WORKFLOW]: {
    field: 'dueDate',
    direction: SmartTable.Order.ASC,
  },
  [WORKFLOW_MODULE_ID.COMPLIANCE]: {
    field: 'stepDueDate',
    direction: SmartTable.Order.ASC,
  },
};

export class TableFilters {
  page = 1;
  page_size = 10;
  search = '';
  orderId: string;
  orderType: SmartTable.Order;
  filters: TodoListCustomFilter;

  constructor(workflowTypeId: WORKFLOW_MODULE_ID = WORKFLOW_MODULE_ID.TESTING_WORKFLOW, certemyTasks: boolean = false) {
    const { field, direction } = certemyTasks
      ? DEFAULT_SORT_OPTION['certemy-tasks']
      : DEFAULT_SORT_OPTION[workflowTypeId];
    this.orderId = field;
    this.orderType = direction;
    this.filters = {
      complianceStatusIds: [TO_DO_COMPLIANCE_STATUS.IN_PROGRESS],
      workflowTypeId,
      ...(!certemyTasks && {
        stepDueDateUnitAmount: DEFAULT_STEP_DUE_DATE_RANGE_FILTER_VALUE,
      }),
    };
  }
}

export const AvailableTestingFiltersConfig = [
  {
    filterType: SmartFilters.SelectFilter,
    name: 'workflowName',
    label: 'Workflow Name',
    defaultValue: null,
    optionsParam: 'workflowNames',
  },
  {
    filterType: SmartFilters.SelectFilter,
    name: 'examName',
    label: 'Exam Name',
    defaultValue: null,
    optionsParam: 'examNames',
  },
  {
    filterType: SmartFilters.SelectFilter,
    name: 'phaseName',
    label: 'Phase',
    defaultValue: null,
    optionsParam: 'phaseNames',
  },
  {
    filterType: SmartFilters.SelectFilter,
    name: 'stepName',
    label: 'Step',
    defaultValue: null,
    optionsParam: 'stepNames',
  },
  {
    filterType: SmartFilters.SelectFilter,
    name: 'organizationName',
    label: 'Organization Name',
    defaultValue: null,
    optionsParam: 'organizationNames',
  },
  {
    filterType: SmartFilters.SelectFilter,
    name: 'stepDueDateUnitAmount',
    label: 'Step Due Date Range',
    defaultValue: DEFAULT_STEP_DUE_DATE_RANGE_FILTER_VALUE,
    optionsParam: 'stepDueDateUnitAmount',
    options: TESTING_DEFAULT_STEP_DUE_DATE_RANGE_FILTER_OPTIONS,
  },
];

export const GENERIC_TODO_LIST_POLICY_PERMISSIONS: GenericWorkflowTodoListPolicyPermissions = {
  ['certemy-tasks']: {
    DYNAMIC: { name: 'DYNAMIC', url: 'compliance/todo_verification/dynamic_form', action: 'reject_approve' },
    EDUCATION: { name: 'EDUCATION', url: 'compliance/todo_verification/education', action: 'reject_approve' },
    LICENSE_VERIFICATION: {
      name: 'LICENSE_VERIFICATION',
      url: 'compliance/todo_verification/license_verification',
      action: 'reject_approve',
    },
    LICENSE_RENEWAL: {
      name: 'LICENSE_RENEWAL',
      url: 'compliance/todo_verification/license_renewal',
      action: 'reject_approve',
    },
    ONBOARDING: { name: 'ONBOARDING', url: 'compliance/todo_verification/onboarding', action: 'reject_approve' },
    ONBOARDING_PSV_VERIFICATION: {
      name: 'ONBOARDING_PSV_VERIFICATION',
      url: 'compliance/todo_verification/onboarding_psv_verification',
      action: 'reject_approve',
    },
    usersList: { name: 'usersList', url: 'settings/users/list', action: 'view' },
  },
};

export const TODO_LIST_POLICY_PERMISSIONS: WorkflowTodoListPolicyPermissions = {
  [WORKFLOW_MODULE_ID.TESTING_WORKFLOW]: {
    ADA: { name: 'ADA', url: 'testing/todo_verification/ada', action: 'reject_approve' },
    QUALIFICATION: { name: 'QUALIFICATION', url: 'testing/todo_verification/qualification', action: 'reject_approve' },
    DYNAMIC: { name: 'DYNAMIC', url: 'testing/todo_verification/dynamic_form', action: 'reject_approve' },
    FOLLOW_UP: { name: 'FOLLOW_UP', url: 'testing/todo_verification/follow_up', action: 'reject_approve' },
    TEST_REGISTRATION: {
      name: 'TEST_REGISTRATION',
      url: 'testing/todo_verification/test_registration',
      action: 'reject_approve',
    },
    usersList: { name: 'usersList', url: 'settings/users/list', action: 'view' },
  },
  [WORKFLOW_MODULE_ID.COMPLIANCE]: {
    DYNAMIC: { name: 'DYNAMIC', url: 'compliance/todo_verification/dynamic_form', action: 'reject_approve' },
    EDUCATION: { name: 'EDUCATION', url: 'compliance/todo_verification/education', action: 'reject_approve' },
    LICENSE_VERIFICATION: {
      name: 'LICENSE_VERIFICATION',
      url: 'compliance/todo_verification/license_verification',
      action: 'reject_approve',
    },
    LICENSE_RENEWAL: {
      name: 'LICENSE_RENEWAL',
      url: 'compliance/todo_verification/license_renewal',
      action: 'reject_approve',
    },
    ONBOARDING: { name: 'ONBOARDING', url: 'compliance/todo_verification/onboarding', action: 'reject_approve' },
    ONBOARDING_PSV_VERIFICATION: {
      name: 'ONBOARDING_PSV_VERIFICATION',
      url: 'compliance/todo_verification/onboarding-psv-verification',
      action: 'reject_approve',
    },
    usersList: { name: 'usersList', url: 'settings/users/list', action: 'view' },
  },
};

type GenericWorkflowTodoListPolicyPermissions = { [key: string]: Record<string, PolicyObj> };
type WorkflowTodoListPolicyPermissions = { [key in WORKFLOW_MODULE_ID]?: Record<string, PolicyObj> };
